import React, { memo, useCallback, useMemo } from "react";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useUsers } from "modules/providers/users-provider";
import { useMutation } from "@apollo/client";
import { CALLOUT_KEY } from "modules/utils/Constants";
import colors from "app/colors";
import Toast from "modules/toasts/toast";
import { GreenCheckIcon, AdminUndoTrashIcon } from "modules/icons";
import { getUserFullName } from "modules/utils/Functions";
import {
  AdminEditUserMutation,
  AdminEditUserMutationVariables,
} from "gql/graphql";
import ADMIN_EDIT_USER_MUTATION from "./_networking/admin-edit-user";
import { useOverlay } from "modules/providers/overlay-provider";
import useDisplayToast from "modules/hooks/use-display-toast";

interface IProps {
  userId?: string;
}

const AdminReactivateUserModal = ({ userId }: IProps) => {
  const { closeModal } = useOverlay();
  const { usersById } = useUsers();
  const { displayToast } = useDisplayToast();

  const [adminEditUserMutationAPI] = useMutation<
    AdminEditUserMutation,
    AdminEditUserMutationVariables
  >(ADMIN_EDIT_USER_MUTATION);

  const onReactivateUserClicked = useCallback(() => {
    if (!userId) return;
    adminEditUserMutationAPI({
      variables: {
        id: userId,
        input: {
          active: true,
        },
      },
    });
    closeModal();
    displayToast(() => (
      <Toast
        message="User Reactivated"
        leftIcon={<AdminUndoTrashIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
  }, [adminEditUserMutationAPI, closeModal, userId, displayToast]);

  const user = useMemo(() => {
    if (userId) {
      return usersById[userId];
    }
  }, [userId, usersById]);

  return (
    <Modal isOpen onClose={closeModal} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <VStack>
            <GreenCheckIcon boxSize={140} />
            <Text fontSize={20} fontWeight={700}>
              Reactivate User?
            </Text>
            <Text>Are you sure you want to reactivate the user</Text>
            <Text
              fontWeight={700}
            >{`${getUserFullName(user)} (${CALLOUT_KEY}${user?.callout})?`}</Text>
            <HStack>
              <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                No
              </Button>
              <Button height={8} w={28} onClick={onReactivateUserClicked}>
                Yes
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminReactivateUserModal);
